import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  CondimentInterface,
  CondimentsListPropsInterface,
} from "../../../entities/Condiment";
import { TranslationContextInterface } from "../../../entities/Language";
import { TranslationContext } from "../../../context/TranslationContext";
import { getCurrentLanguageId } from "../../../lang/utils";
import { useAppDispatch } from "../../../store/store";
import { getCondiments, getIsLoadingCondiments } from "../selectors";
import { fetchCondiments } from "../actions";
import { getValueForKey } from "../../../context/translationsUtils";
import { CondimentListStyled } from "./CondimentListStyled";
import CondimentWrapper from "../CondimentWrapper/CondimentWrapper";
import CondimentSkeleton from "../CondimentSkeleton";
import { getCategoriesIsLoading } from "../../Categories/slice/selectors";
import { sortByDirection } from "../../../utils/sortByDirection";
import SkeletonComponent from "../../SkeletonComponent/SkeletonComponent";
import { VIEW_TYPE } from "../../../entities/Filter";
import { getMixesByFuchs } from "../../MixByFuchs/selectors";
import { MixInterface } from "../../../entities/Mix";
import MixWrapper from "../../Mix/MixWrapper";

const CondimentsList: React.FC<CondimentsListPropsInterface> = ({
  categoryId,
  onSelect,
  sort,
  isPredefinedOption = false,
  view = VIEW_TYPE.GRID,
  isCategoriesCondiments = false,
  categoryName,
  disableSelected = true,
}: CondimentsListPropsInterface) => {
  const { translations }: TranslationContextInterface =
    useContext(TranslationContext);
  const languageId = getCurrentLanguageId();
  const dispatch = useAppDispatch();
  const { spices = [], herbs = [] } = useSelector(getCondiments);
  const mixesByFuchs = useSelector(getMixesByFuchs);
  const [sortedSpices, setSortedSpices] = useState<CondimentInterface[]>([]);
  const [sortedHerbs, setSortedHerbs] = useState<CondimentInterface[]>([]);
  const [sortedMixesByFuchs, setSortedMixesByFuchs] = useState<MixInterface[]>(
    [],
  );
  const isLoading = useSelector(getIsLoadingCondiments);
  const isLoadingCategories = useSelector(getCategoriesIsLoading);

  useEffect(() => {
    if (categoryId) {
      dispatch(
        fetchCondiments({
          params: {
            categoryId,
          },
        }),
      );
    } else {
      dispatch(fetchCondiments({}));
    }
  }, [languageId, dispatch, categoryId]);

  useEffect(() => {
    if (spices.length || herbs.length) {
      const sortedByDirectionSpices = sortByDirection([...spices], sort);
      const sortedByDirectionHerbs = sortByDirection([...herbs], sort);
      
      setSortedSpices(sortedByDirectionSpices);
      setSortedHerbs(sortedByDirectionHerbs);
    } else {
      const sortedByDirectionSpices = sortByDirection([], sort);
      const sortedByDirectionHerbs = sortByDirection([], sort);
      
      setSortedSpices(sortedByDirectionSpices);
      setSortedHerbs(sortedByDirectionHerbs);
    }

    if (mixesByFuchs.length) {
      const sortedByDirectionMixesByFuchs = sortByDirection(
        [...mixesByFuchs],
        sort,
      );
      setSortedMixesByFuchs(sortedByDirectionMixesByFuchs);
    } else {
      const sortedByDirectionMixesByFuchs = sortByDirection(
        [],
        sort,
      );
      setSortedMixesByFuchs(sortedByDirectionMixesByFuchs);
    }
    
  }, [sort, spices, herbs, mixesByFuchs]);

  const chooseSpicesTitle = categoryName
    ? `${categoryName} - ${getValueForKey(
        translations,
        "custom.choose_spices_hint",
      )}`
    : getValueForKey(translations, "custom.choose_spices_hint");
  const chooseHerbsTitle = categoryName
    ? `${categoryName} - ${getValueForKey(
        translations,
        "custom.choose_herbs_hint",
      )}`
    : getValueForKey(translations, "custom.choose_herbs_hint");

  return (
    <CondimentListStyled>
      <SkeletonComponent
        isLoading={isLoading || isLoadingCategories}
        skeleton={<CondimentSkeleton />}
      >
        {!isPredefinedOption && spices.length ? (
          <CondimentWrapper
            title={chooseSpicesTitle}
            condiments={sortedSpices}
            onSelect={onSelect}
            view={view}
            isCategoriesCondiments={isCategoriesCondiments}
            disableSelected={disableSelected}
          />
        ) : (
          <></>
        )}
        {!isPredefinedOption && herbs.length ? (
          <CondimentWrapper
            title={chooseHerbsTitle}
            condiments={sortedHerbs}
            onSelect={onSelect}
            view={view}
            isCategoriesCondiments={isCategoriesCondiments}
            disableSelected={disableSelected}
          />
        ) : (
          <></>
        )}
        {isPredefinedOption && mixesByFuchs.length ? (
          <MixWrapper
            title={chooseSpicesTitle}
            mixes={sortedMixesByFuchs}
            view={view}
            key={categoryId}
          />
        ) : (
          <></>
        )}
      </SkeletonComponent>
    </CondimentListStyled>
  );
};

export default CondimentsList;
